import React from "react";

import { Chip, Text } from "@performyard/py-components";
import type { WorkflowDetailTodoStatus, WorkflowTodoStatusCounts } from "@performyard/service-contracts";

import { DETAIL_TODO_STATUS_CONFIG } from "./statuses";

// Progress reads left-to-right from done to not-started.
const SEGMENT_ORDER: { status: WorkflowDetailTodoStatus; countKey: keyof WorkflowTodoStatusCounts }[] = [
	{ status: "completed", countKey: "completed" },
	{ status: "awaiting_signatures", countKey: "awaitingSignatures" },
	{ status: "overdue", countKey: "overdue" },
	{ status: "incomplete", countKey: "incomplete" },
];

type WorkflowProgressBarProps = {
	counts: WorkflowTodoStatusCounts;
	totalCount: number;
};

/**
 * Overall workflow progress: a headline completion percentage, a chip legend of the
 * per-status counts, and a single stacked bar splitting the workflow's to-dos across
 * completed / in progress / overdue / incomplete.
 *
 * The bar is `aria-hidden` — it is a redundant visual of the legend chips beside it,
 * which already carry every count as text.
 */
const WorkflowProgressBar = ({ counts, totalCount }: WorkflowProgressBarProps) => {
	const segments = SEGMENT_ORDER.map(({ status, countKey }) => ({
		status,
		count: counts[countKey],
		...DETAIL_TODO_STATUS_CONFIG[status],
	}));

	const percentComplete = totalCount > 0 ? Math.round((counts.completed / totalCount) * 100) : 0;

	return (
		<div
			data-testid="workflow-progress-bar"
			className="flex flex-col gap-3 rounded-lg border border-solid border-neutral-2 bg-white p-4"
		>
			<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
				<div className="flex items-center gap-1.5 border-r border-solid border-neutral-2 pe-4">
					<Text size="heading-4" weight="semi-bold" color="neutral-9">
						{`${percentComplete}%`}
					</Text>
					<Text size="paragraph-3" weight="regular" color="neutral-7">
						Complete
					</Text>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{segments.map((segment) => (
						<Chip
							key={segment.status}
							label={`${segment.count} ${segment.label}`}
							color={segment.chipColor}
							size={20}
						/>
					))}
				</div>
			</div>
			<div className="flex h-3 w-full overflow-hidden rounded-full bg-neutral-1" aria-hidden="true">
				{totalCount > 0 &&
					segments
						.filter((segment) => segment.count > 0)
						.map((segment) => (
							<div
								key={segment.status}
								data-testid={`progress-segment-${segment.status}`}
								className={segment.barClassName}
								style={{ width: `${(segment.count / totalCount) * 100}%` }}
							/>
						))}
			</div>
		</div>
	);
};

export default WorkflowProgressBar;
